import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit, ViewEncapsulation } from '@angular/core';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import { buildProviders, Coerce } from '@kalidea/kaligraphi';
import { AbstractControl, UntypedFormControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, Observable, of, Subscription } from 'rxjs';
import {
    CleRepartitionModelDto,
    MutationGetListeClesRepartitionModelArgs,
    TypeCleRepartitionEnum
} from 'src/graphql/generated-types';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import gql from 'graphql-tag';
import { tap } from 'rxjs/operators';
import { wheneverEnd } from 'src/app/utils/operators.utils';
import { arrayOnlyOne } from 'src/app/utils/array.utils';

@Component({
    selector: 'app-shared-analytical-plan-distributer-by-account',
    template: `
        <app-shared-analytical-plan-distributer [kalLoading]="loading"
                                                [formControl]="formControl"
                                                [amount]="amount"
                                                [shouldHideAmountColumn]="shouldHideAmountColumn">
        </app-shared-analytical-plan-distributer>`,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(SharedAnalyticalPlanDistributerByAccountComponent),
    standalone: false
})
export class SharedAnalyticalPlanDistributerByAccountComponent extends CustomFormControlAccess implements OnInit {

    @Input() amount = 0;

    @Input()
    @Coerce('boolean')
    shouldHideAmountColumn = false;

    formControl = new UntypedFormControl(null);

    private subscription: Subscription;

    constructor(private readonly graphqlService: GraphqlService,
                private readonly cdr: ChangeDetectorRef) {
        super();
    }

    private _accountId = new BehaviorSubject<number>(null);

    get accountId(): number {
        return this._accountId.getValue();
    }

    @Input()
    set accountId(accountId: number) {
        this._accountId.next(accountId);
    }

    get accountId$(): Observable<number> {
        return this._accountId.asObservable();
    }

    private _distributionKeys = new BehaviorSubject<CleRepartitionModelDto[]>(null);

    get distributionKeys(): CleRepartitionModelDto[] {
        return this._distributionKeys.getValue();
    }

    get distributionKeys$(): Observable<CleRepartitionModelDto[]> {
        return this._distributionKeys.asObservable();
    }

    private _loading = false;

    get loading(): boolean {
        return this._loading;
    }

    set loading(value: boolean) {
        this._loading = value;
        this.cdr.markForCheck();
    }

    writeValue(value: CleRepartitionModelDto[]): void {
        this._distributionKeys.next(value);
        super.writeValue(value);
    }

    validate(c: AbstractControl): Observable<{}> {
        return of(this.formControl.valid && !!this.accountId ? null : {accountAnalyticalRepartition: true});
    }

    private fetchDistributionKeysByAccount(): void {
        if(!this.accountId) {
            this.initSubscription();
            return;
        }

        this.loading = true;

        this.watch$ = this.graphqlService.mutateOne<'GetListeClesRepartitionModel', MutationGetListeClesRepartitionModelArgs>({
            mutation: gql`
            mutation GetListeClesRepartitionModel($contexte: ContextInfosInput, $criteres: CriteresCompteSectionAnalytiqueDTOInput) {
                GetListeClesRepartitionModel(
                    contexte: $contexte
                    criteres: $criteres
                ) {
                    CleRepartitionModelDTO {
                        IdCompte
                        IdPlanAnalytique
                        IdSectionAnalytique
                        Montant
                        Pourcentage
                    }
                }
            }`,
            variables: {
                criteres: {
                    CompteId: this.accountId,
                    VisionCle: TypeCleRepartitionEnum.Toutes
                }
            }
        }).pipe(
            tap(GetListeClesRepartitionModel => {
                const distributionKeyList = GetListeClesRepartitionModel?.CleRepartitionModelDTO || [];
                const value = (arrayOnlyOne(distributionKeyList) && !distributionKeyList[0].IdSectionAnalytique) ? [] : distributionKeyList;

                this.formControl.patchValue(this.distributionKeys || value, {emitEvent: false});
                this.notifyUpdate(this.formControl.value);
                this.initSubscription();
            }),
            wheneverEnd(() => this.loading = false)
        );
    }

    private initSubscription(): void {
        this.subscription = this.formControl.valueChanges.pipe(
            this.untilDestroyed,
            tap(formValues => this.notifyUpdate(formValues))
        ).subscribe();
    }

    ngOnInit(): void {
        this.watch$ = combineLatest([
            this.accountId$,
            this.distributionKeys$
        ]).pipe(
            tap(() => {
                this.subscription?.unsubscribe();

                if (!this.distributionKeys) {
                    this.fetchDistributionKeysByAccount();
                } else {
                    this.formControl.patchValue(this.distributionKeys, {emitEvent: false});
                    this.initSubscription();
                }
            }),
        );
    }

}
